<script lang="ts">
  // d3 imports
  import { timeParse, timeFormat } from 'd3-time-format';
  import { scaleLinear, scaleTime } from 'd3-scale';
  import { extent, max } from 'd3-array';
  import { line } from 'd3-shape';

  // Utils
  import { monthFormatter } from '$utils/formatter';

  // Types
  import type { MultipleLineChartProps } from './types';
  import { getRollingAverageData } from '../chartUtils';
  import { get } from 'svelte/store';

  import { onMount } from 'svelte';

  let {
    data,
    categoryKey,
    height = 400,
    zeroBased = true,
    unit = '%',
    dateParser,
    dateFormatter,
    xValKey,
    yValKey,
    customYDomain,
    customXDomain,
    customXTicks,
    xTickFormatter,
    yTickFormatter,
    id,
    class: cls = 'multi-line-chart',
    xTicks = 2,
    yTicks = 5,
    endDotDiameter = 10,
    lineColours,
    endValLabels,
    endValMargin = 0,
    highlightCategory,
    annotations,
    rollingAvg,
    categoriesOrder,
    tariffDate,
    embedded = false,
  }: MultipleLineChartProps<Record<string, unknown>> = $props();

  let scrollY = $state(0);
  let farthestScroll = $state(0);
  let relativeYPosition = $state(-100);

  $effect(() => {
    if (scrollY > farthestScroll) {
      farthestScroll = scrollY;
    }
  });

  // Set d3 date parser and formatter
  let dateParserFn = timeParse(dateParser);
  let dateFormatterFn = timeFormat(dateFormatter);

  // Get a list of values for `categoryKey` from the data
  let categories = categoriesOrder || [
    ...new Set(data.map((d) => d[categoryKey])),
  ];

  // Set the y and x axis domains and scales
  const yDomain = customYDomain || extent(data.map((x) => +x[yValKey]));

  const yScale =
    zeroBased ?
      scaleLinear().domain([0, yDomain[1]]).range([100, 0])
    : scaleLinear().domain(yDomain).range([100, 0]);

  const xDomain =
    customXDomain ||
    extent(
      data.map((x) => (dateParser ? dateParserFn(x[xValKey]) : x[xValKey]))
    );

  const xScale =
    dateParser ?
      scaleTime().domain(xDomain).range([0, 100])
    : scaleLinear().domain(xDomain).range([0, 100]);

  // Set the line generator, which will be used to create the path for the line
  const lineGenerator = line()
    .x((d) => xScale(dateParser ? dateParserFn(d[xValKey]) : d[xValKey]))
    .y((d) => yScale(+d[yValKey]));

  // Set the ticks
  const xTicksArr = customXTicks || xScale.ticks(xTicks);
  const yTicksArr = yScale.ticks(yTicks);

  function getCategoryData(categoryVal, categoryKey, data, rollingAvg) {
    const filteredData = data.filter((d) => d[categoryKey] === categoryVal);

    // If `rollingAvg` is set, we need to calculate the rolling average
    if (rollingAvg)
      return getRollingAverageData(filteredData, yValKey, rollingAvg);

    return filteredData;
  }

  // Function that gets the x position of the last data point for each category data, using categoryData as a prop
  function getLastDataPointX(categoryData) {
    const lastXVal = categoryData[categoryData.length - 1][xValKey];
    return xScale(dateParser ? dateParserFn(lastXVal) : lastXVal);
  }

  //function taht gets the right y value for the data on the day of April 2, the tariff announcement{
  function getTariffDateY(categoryData) {
    if (!tariffDate) return null;
    if (!dateParser) return null;
    const tariffDateX = categoryData.find(
      (d) =>
        dateFormatterFn(dateParserFn(d[xValKey])) ===
        dateFormatterFn(tariffDate)
    );

    return yScale(tariffDateX[yValKey]);
  }

  onMount(() => {
    const container = document.querySelector(
      `.multi-line-chart-container#${id}`
    );

    const topChart = document.querySelector(
      '.multi-line-chart-container#presidency-sp'
    );

    if (container && topChart) {
      const containerYPosition =
        container.getBoundingClientRect().top + window.scrollY;

      const topChartYPosition =
        topChart.getBoundingClientRect().top + window.scrollY;

      relativeYPosition =
        id != 'presidency-sp' ?
          containerYPosition - topChartYPosition - 100
        : containerYPosition - topChartYPosition;
    }
  });
</script>

<svelte:window bind:scrollY />
<div class="multi-line-chart-container {cls}" {id} style="height: {height}px;">
  <!-- base layer -->
  <svg
    class="layer"
    viewBox="0 0 100 100"
    preserveAspectRatio="none"
    style="margin-right:{endValMargin}px;width: calc(100% - {endValMargin +
      30}px);"
  >
    <!-- ticks -->
    {#each yTicksArr as tick}
      <line
        class="tick y"
        class:zero={tick == 0}
        x1="-50"
        x2="100"
        y1={yScale(tick)}
        y2={yScale(tick)}
      ></line>
    {/each}

    {#each xTicksArr as tick}
      <line class="tick x" y1="100" y2="104" x1={xScale(tick)} x2={xScale(tick)}
      ></line>
    {/each}

    <!-- Draw lines for each category -->
    {#each categories as categoryVal}
      {@const categoryData = getCategoryData(
        categoryVal,
        categoryKey,
        data,
        rollingAvg
      )}
      {@const lineColour =
        lineColours?.find((c) => c[categoryKey] === categoryVal)?.colour ||
        'orange'}
      <!-- print out categoryData on the page -->
      {#if categoryData}
        <path
          class="multi-line-chart data-line bg {categoryKey} {categoryVal}"
          class:highlight={highlightCategory == categoryVal}
          class:animate={farthestScroll > relativeYPosition &&
            highlightCategory == categoryVal &&
            !embedded}
          class:embedded
          d={lineGenerator(categoryData)}
          style="stroke: #fff;"
        />
        <path
          class="multi-line-chart data-line {categoryKey} {categoryVal}"
          class:highlight={highlightCategory == categoryVal}
          class:animate={farthestScroll > relativeYPosition &&
            highlightCategory == categoryVal &&
            !embedded}
          class:embedded
          d={lineGenerator(categoryData)}
          style="stroke: {lineColour};"
        />
      {/if}

      {#if tariffDate && categoryVal == highlightCategory}
        <line
          class="tariff-line"
          class:animate={farthestScroll > relativeYPosition &&
            highlightCategory == categoryVal &&
            !embedded}
          class:embedded
          x1={xScale(tariffDate)}
          x2={xScale(tariffDate)}
          y1={getTariffDateY(categoryData) - 2}
          y2={getTariffDateY(categoryData) - 7}
        ></line>
      {/if}
    {/each}
  </svg>

  <!-- top layer -->
  <div
    class="layer"
    style="margin-right:{endValMargin}px; width: calc(100% - {endValMargin +
      30}px);"
  >
    {#each yTicksArr as tick, i}
      <p
        class="tick-label y on-chart-text font-sans text-secondary"
        style="top: {yScale(tick)}%; left:0%"
      >
        {yTickFormatter ? yTickFormatter(tick) : tick}{(
          i == yTicksArr.length - 1
        ) ?
          unit
        : ''}
      </p>
    {/each}
    {#each xTicksArr as tick}
      <p
        class="tick-label x on-chart-text font-sans text-secondary"
        style="left: calc({xScale(tick)}% - 17px); top:calc(100% + 15px)"
      >
        {#if xTickFormatter}
          {xTickFormatter(tick)}
        {:else if typeof tick === 'number'}
          {tick}
        {:else}
          {monthFormatter(dateFormatter ? dateFormatterFn(tick) : tick)}
        {/if}
      </p>
    {/each}

    {#each categories as categoryVal}
      {@const categoryData = getCategoryData(
        categoryVal,
        categoryKey,
        data,
        rollingAvg
      )}
      {@const lastDataPointX = getLastDataPointX(categoryData)}

      {@const lineColour =
        lineColours?.find((c) => c[categoryKey] === categoryVal)?.colour ||
        'orange'}
      {#if categoryData}
        <span
          class="end-dot {cls} {categoryKey} {categoryVal}"
          class:highlight={highlightCategory == categoryVal}
          class:animate={farthestScroll > relativeYPosition &&
            highlightCategory == categoryVal &&
            !embedded}
          class:embedded
          style="left: {lastDataPointX}%; top:{yScale(
            categoryData[categoryData.length - 1][yValKey]
          )}%; background-color:{lineColour}; height:{(
            categoryVal == highlightCategory
          ) ?
            endDotDiameter * 1.2
          : endDotDiameter}px; width:{categoryVal == highlightCategory ?
            endDotDiameter * 1.2
          : endDotDiameter}px;"
        ></span>
        {#if tariffDate && categoryVal == highlightCategory}
          <span
            class="end-dot tariff-date"
            class:animate={farthestScroll > relativeYPosition &&
              highlightCategory == categoryVal &&
              !embedded}
            class:embedded
            style="left: {xScale(tariffDate)}%; top:{getTariffDateY(
              categoryData
            )}%; background-color:{lineColour}; height:{endDotDiameter *
              1.2}px; width:{endDotDiameter * 1.2}px;"
          ></span>
          <p
            class="on-chart-text font-sans tariff-anno"
            class:animate={farthestScroll > relativeYPosition &&
              highlightCategory == categoryVal &&
              !embedded}
            class:embedded
            style="left: {xScale(tariffDate)}%; top:{getTariffDateY(
              categoryData
            )}%;"
          >
            Tariff announcement
          </p>
        {/if}
        <p
          class="on-chart-text font-sans font-bold end-val notes {cls} {categoryKey} {categoryVal}"
          class:highlight={highlightCategory == categoryVal}
          class:animate={farthestScroll > relativeYPosition &&
            highlightCategory == categoryVal &&
            !embedded}
          class:embedded
          style="left: calc({lastDataPointX}% + 8px); top:{yScale(
            categoryData[categoryData.length - 1][yValKey]
          )}%; color:{lineColour}"
        >
          {#if endValLabels}
            {endValLabels.find((c) => c[categoryKey] === categoryVal)?.label}
          {:else}
            {categoryVal}
          {/if}
        </p>
      {/if}
    {/each}

    {#if annotations}
      {#each annotations as anno}
        <div
          class="annotation {cls}"
          style="top:{yScale(anno.yPos)}%; left:{xScale(
            anno.xPos
          )}%; width:{anno.width}px"
        >
          {anno.text}
        </div>
      {/each}
    {/if}
  </div>
</div>

<style lang="scss">
  @use '@reuters-graphics/graphics-components/dist/scss/mixins' as mixins;

  .multi-line-chart-container {
    width: 100%;
    position: relative;
    overflow: hidden;
    // border: 2px solid #f0f;
  }

  .layer {
    position: absolute;
    top: 0;
    left: 0;
    margin-bottom: 40px;
    margin-top: 20px;
    margin-left: 30px;
    height: calc(100% - 70px);
    overflow: visible;
    // border: 2px dotted blue;
  }

  .data-line {
    fill: none;
    vector-effect: non-scaling-stroke;
    stroke-linejoin: round;
    stroke-linecap: butt;
    stroke-width: 2px;
    // mix-blend-mode: multiply;
  }

  .data-line.highlight {
    stroke-width: 3px;
    stroke-dasharray: 1500;
    stroke-dashoffset: 1500;
  }

  .data-line.highlight.embedded {
    stroke-dashoffset: 0 !important;
    stroke-dasharray: 0 !important;
  }

  .data-line.highlight.animate {
    animation: line-anim 2s ease-in forwards;
  }

  .data-line.bg {
    stroke-width: 4px;
    opacity: 0.75;
  }

  .data-line.bg.highlight {
    stroke-width: 5px;
    opacity: 0.75;
  }

  line.tariff-line {
    stroke-width: 1px;
    stroke: var(--tr-dark-grey);
    vector-effect: non-scaling-stroke;
    opacity: 0;
  }

  line.tariff-line.embedded {
    opacity: 1 !important;
  }

  line.tariff-line.animate {
    animation: appear 0.25s linear 1s forwards;
  }

  line.tick {
    fill: none;
    stroke: var(--tr-muted-grey);
    vector-effect: non-scaling-stroke;
  }

  line.tick.x {
    stroke: var(--tr-muted-grey);
  }

  line.tick.y.zero {
    stroke: var(--tr-dark-grey);
  }

  .annotation,
  p.on-chart-text {
    position: absolute;
    font-weight: 300;
  }

  p.tick-label {
    font-size: var(--theme-font-size-sm) !important;
    white-space: nowrap;
    color: #999 !important;
    &.y {
      transform: translate(-30px, -100%);
    }
  }

  p.end-val {
    transform: translate(0, -50%);
    font-size: var(--theme-font-size-sm) !important;
    font-weight: 400 !important;
    white-space: nowrap;
  }

  p.end-val.highlight {
    font-weight: 700 !important;
    opacity: 0;
  }

  p.end-val.highlight.embedded {
    opacity: 1 !important;
  }

  p.end-val.highlight.animate {
    animation: appear 0.5s linear 1.25s forwards;
  }

  p.tariff-anno {
    transform: translate(-50%, calc(-100% - 40px));
    font-size: var(--theme-font-size-xs) !important;
    font-weight: 400 !important;
    color: var(--tr-dark-grey);
    text-align: center;
    width: 100px;
    line-height: 1.2 !important;
    opacity: 0;
  }

  p.tariff-anno.embedded {
    opacity: 1 !important;
  }

  p.tariff-anno.animate {
    animation: appear 0.5s linear 1s forwards;
  }

  span.end-dot {
    border-radius: 50%;
    position: absolute;
    transform: translate(-50%, -50%);
    border: 1px solid white;
  }

  span.end-dot.highlight {
    opacity: 0;
  }

  span.end-dot.highlight.embedded {
    opacity: 1 !important;
  }

  span.end-dot.animate.highlight {
    animation: appear 0.25s linear 1.25s forwards;
  }

  span.end-dot.tariff-date {
    // background-color: white !important;
    // border: 2px solid var(--tr-dark-grey);
    width: 0 !important;
    height: 0 !important;
    border-radius: 0%;
    border-bottom: none;
    border-left: 5px solid transparent !important;
    border-right: 5px solid transparent !important;
    border-top: 10px solid var(--tr-dark-grey) !important;
    background-color: rgba(255, 255, 255, 0) !important;
    transform: translate(-50%, calc(-100% - 5px));
    opacity: 0;
  }

  span.end-dot.tariff-date.embedded {
    opacity: 1 !important;
  }

  span.end-dot.tariff-date.animate {
    animation: appear 0.25s linear 1s forwards;
  }

  @keyframes line-anim {
    to {
      stroke-dashoffset: 0;
    }
  }

  @keyframes appear {
    to {
      opacity: 1;
    }
  }
</style>
